import { useEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import InformationCircle from 'src/icons/InformationCircle';
import moment from 'moment';
import OrderEntryTopNav from 'src/components/order/orderEntry/OrderEntryTopNav';
import orderApi from 'src/api/OrderApi';
import useSettings from 'src/hooks/useSettings';
import useSnackbar from 'src/hooks/useSnackbar';
import {
  ItemClaimType,
  PerfectOrder,
  PerfectOrderDropdownValueResult,
  PerfectOrderFileResponse,
  PerfectOrderInvoiceAddonsResponse,
  PerfectOrderItem,
} from 'src/types/perfectOrder';
import { ItemReturnReason } from 'src/types/itemReturnReasons';
import { getSettingsWidth } from 'src/utils/settingsHelper';
import { Logger } from 'src/utils/logger';
import { buildPerfectOrderComplianceInput, evaluatePerfectOrderCompliance, filterPerfectOrderReturnReasons } from 'src/utils/perfectOrderRules';
import { buildPerfectOrderWorkbook } from 'src/utils/perfectOrderExport';
import { getUniqueUsersByUsername, userHasRole } from 'src/utils/userHelpers';
import { currencyFormatter } from 'src/utils/helperFunctions';
import PerfectOrderItemGrid from './PerfectOrderItemGrid';
import PerfectOrderFilesGrid from './PerfectOrderFilesGrid';
import PerfectOrderNotesGrid from './PerfectOrderNotesGrid';
import { AddedPerfectOrderItem } from 'src/components/order/PerfectOrderItemPicker';
import { useCustomerByNumber } from 'src/hooks/useCustomer';
import { CustomerReps } from 'src/types/customer';
import TypeAhead from 'src/components/controls/TypeAhead';
import useCrm from 'src/hooks/useCrm';
import { UserSimple } from 'src/types/userSimple';
import 'src/theme/partials/order/PerfectOrder.css';

interface CustomerRepRow {
  label: string;
  value: string;
}
interface RepeaterRow {
  id: string;
  value: string;
}
const getCustomerRepRows = (source?: CustomerReps): CustomerRepRow[] =>
  [
    { label: 'Hard Goods Rep', value: source?.hardGoodsRep },
    { label: 'Lawn & Garden Rep', value: source?.lawnGardenRep },
    { label: 'Plant Rep', value: source?.plantRep },
  ]
    .filter((row): row is CustomerRepRow => Boolean(row.value?.trim()))
    .map((row) => ({ ...row, value: row.value.trim() }));

const Field: FC<{ label: string; value: string | number | boolean | null | undefined; emptyText?: string }> = ({ label, value, emptyText = 'N/A' }) => (
  <div>
    <strong>{label}:</strong> <span className="view-order__column__val px-1">{value === null || value === undefined || value === '' ? emptyText : String(value)}</span>
  </div>
);

const DateField: FC<{ label: string; value: string | null | undefined; emptyText?: string }> = ({ label, value, emptyText }) => (
  <Field label={label} value={value ? moment.parseZone(value).format('M/DD/YYYY') : undefined} emptyText={emptyText} />
);

const formatAddonAmount = (value?: number | string): string => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? currencyFormatter.format(numericValue) : String(value);
};

const normalizePerfectOrder = (order: PerfectOrder): PerfectOrder => ({
  ...order,
  isDnsNeeded: order.isDnsNeeded ?? false,
  isCreditNeeded: order.isCreditNeeded ?? false,
});

let nextRepeaterRowId = 0;
const createRepeaterRowId = (): string => `r-${++nextRepeaterRowId}`;

const parseRepeaterValue = (value?: string | null): RepeaterRow[] => {
  const parts = (value ?? '').split(',').map((part) => part.trim());
  const rows = parts.filter((part) => part.length > 0);
  if (rows.length === 0) {
    return [{ id: createRepeaterRowId(), value: '' }];
  }
  return rows.map((part) => ({ id: createRepeaterRowId(), value: part }));
};

const joinRepeaterRows = (rows: RepeaterRow[]): string =>
  rows
    .map((row) => row.value.trim())
    .filter((value) => value.length > 0)
    .join(',');

const normalizePerfectOrderItemsForSave = (items?: PerfectOrderItem[]): PerfectOrderItem[] | undefined =>
  items
    ?.filter((item) => item.pendingDelete !== true)
    .map((item) => {
      const { selected: _selected, pendingDelete: _pendingDelete, kitParentItemNumber: _kitParentItemNumber, ...itemToSave } = item;
      return itemToSave;
    });

const isSamePerfectOrderItem = (item: PerfectOrderItem, target: PerfectOrderItem): boolean =>
  item === target || (item.perfectOrderLineId != null && target.perfectOrderLineId != null && item.perfectOrderLineId === target.perfectOrderLineId);

const getPerfectOrderItemKey = (item: PerfectOrderItem): string => {
  if (item.perfectOrderLineId != null && item.perfectOrderLineId > 0) {
    return `id:${item.perfectOrderLineId}`;
  }
  if (item.retOrderLine != null) {
    return `line:${item.retOrderLine}`;
  }
  return `item:${item.itemNumber?.toLowerCase() ?? ''}:${item.vendorNumber ?? ''}`;
};

const BooleanCheckboxField: FC<{
  label: string;
  value: boolean | null | undefined;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  helperText?: string;
  children?: ReactNode;
}> = ({ label, value, onChange, disabled, helperText, children }) => {
  const checkboxId = `checkbox-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <FormControl component="fieldset" sx={{ my: 0.75, width: '100%' }} disabled={disabled}>
      <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <FormLabel
          htmlFor={checkboxId}
          component="label"
          sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 700, mb: 0, minWidth: '115px', flexShrink: 0, cursor: disabled ? 'default' : 'pointer' }}
        >
          {label}
          {/* {helperText && (
            <Tooltip title={helperText}>
              <Box component="span" sx={{ ml: 0.5, display: 'inline-flex', verticalAlign: 'middle' }}>
                <InformationCircle fontSize="small" color="error" />
              </Box>
            </Tooltip>
          )} */}
          <Checkbox id={checkboxId} checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        </FormLabel>
        {children}
      </Box>
      {helperText && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.25 }}>{helperText}</Box>}
    </FormControl>
  );
};

const RepeaterField: FC<{
  label: string;
  addLabel: string;
  removeAriaLabel: string;
  value: string;
  onChange: (joined: string) => void;
}> = ({ label, addLabel, removeAriaLabel, value, onChange }) => {
  const [rows, setRows] = useState<RepeaterRow[]>(() => parseRepeaterValue(value));
  const [autoFocusRowId, setAutoFocusRowId] = useState<string | null>(null);

  const applyRows = (nextRows: RepeaterRow[]) => {
    setRows(nextRows);
    onChange(joinRepeaterRows(nextRows));
  };

  const handleChange = (id: string, nextValue: string) => {
    applyRows(rows.map((row) => (row.id === id ? { ...row, value: nextValue } : row)));
  };

  const handleAdd = () => {
    const id = createRepeaterRowId();
    setRows((prev) => [...prev, { id, value: '' }]);
    setAutoFocusRowId(id);
  };

  const handleRemove = (id: string) => {
    const next = rows.filter((row) => row.id !== id);
    applyRows(next.length > 0 ? next : [{ id: createRepeaterRowId(), value: '' }]);
  };

  return (
    <Box>
      {rows.map((row, index) => (
        <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label={index === 0 ? label : `${label} ${index + 1}`}
            value={row.value}
            autoFocus={row.id === autoFocusRowId}
            onChange={(e) => handleChange(row.id, e.target.value)}
          />
          <IconButton size="small" aria-label={removeAriaLabel} onClick={() => handleRemove(row.id)} disabled={rows.length === 1 && !row.value}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button sx={{ mt: -2 }} size="small" variant="text" startIcon={<AddCircleOutlineIcon fontSize="small" />} onClick={handleAdd}>
        {addLabel}
      </Button>
    </Box>
  );
};

const DropdownField: FC<{
  label: string;
  value: number | '';
  options: PerfectOrderDropdownValueResult[];
  onChange: (event: SelectChangeEvent<number | ''>) => void;
  disabled?: boolean;
}> = ({ label, value, options, onChange, disabled }) => (
  <FormControl fullWidth size="small" sx={{ my: 0.75 }} disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select<number | ''> value={value} label={label} onChange={onChange}>
      <MenuItem value="">None</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.dropdownValueId} value={option.dropdownValueId}>
          {option.dropdownValue || option.dropdownValueId}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PerfectOrderInvoiceAddonsGrid: FC<{ addons: PerfectOrderInvoiceAddonsResponse[] }> = ({ addons }) => {
  if (addons.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <h4 className="mb-1">Invoice Add-ons</h4>
      <TableContainer sx={{ maxHeight: 260 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={110}>Add-on #</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right" width={130}>
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addons.map((addon, index) => (
              <TableRow key={`${addon.addonNo}-${index}`} hover>
                <TableCell>{addon.addonNo}</TableCell>
                <TableCell>
                  <Typography variant="body2">{addon.description || 'N/A'}</Typography>
                </TableCell>
                <TableCell align="right">{formatAddonAmount(addon.addonNetAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const formatPhoneNumber = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const digits = value.replace(/\D/g, '');

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    const core = digits.slice(1);
    return `(${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6)}`;
  }

  return value;
};

const PerfectOrderDetail: FC = () => {
  const { perfectOrderId: idParam } = useParams();
  const contentWidth = useSettings((state) => state.settings.contentWidth);
  const { openSnackBar } = useSnackbar();
  const { allSFAUsers } = useCrm();
  const exportRef = useRef<ExcelExport | null>(null);

  const permissionToEdit: boolean = userHasRole(['Customer Service Admin', 'Customer Service User', 'Admin']);
  const [perfectOrder, setPerfectOrder] = useState<PerfectOrder | null>(null);
  const customer = useCustomerByNumber(perfectOrder?.customerNumber);
  const [dropdownValues, setDropdownValues] = useState<PerfectOrderDropdownValueResult[]>([]);
  const [claimTypes, setClaimTypes] = useState<ItemClaimType[]>([]);
  const [returnReasons, setReturnReasons] = useState<ItemReturnReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filteredAssignedToUsers, setFilteredAssignedToUsers] = useState<UserSimple[] | null>(null);

  useEffect(() => {
    document.title = 'Perfect Order Detail';
    const id = Number(idParam);
    if (!id) {
      setLoadError('Invalid perfect order id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    Promise.all([orderApi.getPerfectOrder(id), orderApi.getPerfectOrderDropdownValues(), orderApi.getItemClaimTypes(), orderApi.getItemReturnReasons()])
      .then(([orderData, dropdownData, claimTypeData, returnReasonData]) => {
        const normalized = normalizePerfectOrder(orderData);
        setPerfectOrder(normalized);
        setDropdownValues(dropdownData || []);
        setClaimTypes(claimTypeData || []);
        setReturnReasons(returnReasonData || []);
      })
      .catch((error) => {
        setPerfectOrder(null);
        setClaimTypes([]);
        setReturnReasons([]);
        setLoadError(`Unable to load Perfect Order #${id}.`);
        openSnackBar(`Unable to load Perfect Order #${id}.`, 'error', 5000);
        Logger.error(`Error loading Perfect Order ${id}`, error);
      })
      .finally(() => setLoading(false));
  }, [idParam, openSnackBar]);

  const compliance = useMemo(() => {
    if (!perfectOrder) return null;
    return evaluatePerfectOrderCompliance(buildPerfectOrderComplianceInput(perfectOrder));
  }, [perfectOrder]);

  const customerReps = useMemo(() => {
    if (!perfectOrder || !customer) return [];

    return getCustomerRepRows({
      hardGoodsRep: customer.hardGoodsRep,
      lawnGardenRep: customer.lawnGardenRep,
      plantRep: customer.plantRep,
    });
  }, [customer, perfectOrder]);

  const creditEligibilityReason = useMemo(() => {
    if (!perfectOrder || !compliance) return undefined;

    const claimType = claimTypes.find((item) => item.claimTypeId === perfectOrder.claimTypeId)?.claimType?.toLowerCase() || '';

    if (claimType.includes('defective') && !compliance.isDefectiveCreditEligible) {
      return 'Ineligible: Invoice is older than 365 days.';
    }

    if (claimType.includes('shortage') && !compliance.isShortageCreditEligible) {
      return 'Ineligible: Invoice is older than 90 days.';
    }

    return undefined;
  }, [perfectOrder, compliance, claimTypes]);

  const handleDropdownFieldChange = (field: 'statusId' | 'rootCauseId', value: number | '') => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const nextValue = value === '' ? undefined : value;

      if (field === 'statusId' && value !== '') {
        const selectedStatus = dropdownValues.find((item) => item.dropdownType === 'Status' && item.dropdownValueId === Number(value));
        const isClosed = selectedStatus?.dropdownValue?.trim().toLowerCase() === 'closed';

        return {
          ...prev,
          [field]: nextValue,
          dateClosed: isClosed ? new Date().toISOString() : prev.dateClosed,
        };
      }

      return {
        ...prev,
        [field]: nextValue,
      };
    });
  };

  const handleAssignedToChange = (userSelected: UserSimple | null) => {
    setPerfectOrder((prev) => (prev ? { ...prev, assignedTo: userSelected?.companyUserId } : prev));
  };

  const handleAssignedToFilterChange = (value: string | null) => {
    const filterText = value?.trim().toUpperCase();
    setFilteredAssignedToUsers(filterText ? assignedToUsers.filter((user) => user.username.toUpperCase().includes(filterText)) : null);
  };

  const handleClaimTypeChange = (value: number | '') => {
    const nextClaimTypeId = value === '' ? undefined : value;

    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const filteredReasons = filterPerfectOrderReturnReasons(returnReasons, claimTypes, nextClaimTypeId || undefined);
      const returnReasonStillValid = prev.returnReasonId && filteredReasons.some((reason) => reason.returnReasonId === prev.returnReasonId);

      return {
        ...prev,
        claimTypeId: nextClaimTypeId,
        returnReasonId: returnReasonStillValid ? prev.returnReasonId : undefined,
        reasonForCreditId: returnReasonStillValid ? prev.reasonForCreditId : undefined,
      };
    });
  };

  const handleReturnReasonChange = (value: number | '') => {
    const reasonId = value === '' ? undefined : value;
    const matched = returnReasons.find((reason) => reason.returnReasonId === reasonId);
    const creditId = matched ? Number(matched.creditReasonType) : undefined;

    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        returnReasonId: reasonId,
        reasonForCreditId: matched && !Number.isNaN(creditId) ? creditId : undefined,
      };
    });
  };

  const handleTextFieldChange = (field: 'buyer', value: string) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleRepeaterFieldChange = (field: 'creditNumber' | 'dnsNumber', joined: string) => {
    setPerfectOrder((prev) => (prev ? { ...prev, [field]: joined || undefined } : prev));
  };

  const handleBooleanFieldChange = (field: 'isDnsNeeded' | 'isCreditNeeded', value: boolean) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const next: PerfectOrder = { ...prev, [field]: value };
      if (field === 'isCreditNeeded' && !value) {
        next.creditNumber = undefined;
      }
      if (field === 'isDnsNeeded' && !value) {
        next.dnsNumber = undefined;
      }
      return next;
    });
  };

  const handleItemFieldChange = (
    targetItem: PerfectOrderItem,
    field: keyof Pick<PerfectOrderItem, 'quantity' | 'customerPrice' | 'quantityUnit'>,
    value: number | string | null
  ) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        perfectOrderItems: (prev.perfectOrderItems || []).map((item: PerfectOrderItem) =>
          isSamePerfectOrderItem(item, targetItem)
            ? {
                ...item,
                [field]: value,
              }
            : item
        ),
      };
    });
  };

  const handleAppendItem = (picked: AddedPerfectOrderItem) => {
    setPerfectOrder((prev) => {
      if (!prev) return prev;
      const currentItems = prev.perfectOrderItems || [];
      const minId = currentItems.reduce((min, item) => Math.min(min, item.perfectOrderLineId ?? 0), 0);
      const tempId = minId <= 0 ? minId - 1 : -1;
      return {
        ...prev,
        perfectOrderItems: [
          ...currentItems,
          {
            perfectOrderLineId: tempId,
            perfectOrderId: prev.perfectOrderId,
            itemNumber: picked.itemNumber,
            vendorNumber: picked.vendorNumber,
            description: picked.description,
            quantity: picked.quantity,
            customerPrice: picked.customerPrice,
            kitParentItemNumber: picked.kitParentItemNumber,
          },
        ],
      };
    });
  };

  const handleAppendKitItems = (parentItem: PerfectOrderItem, pickedItems: AddedPerfectOrderItem[]) => {
    setPerfectOrder((prev) => {
      if (!prev || pickedItems.length === 0) return prev;
      const currentItems = prev.perfectOrderItems || [];
      let nextTempId = currentItems.reduce((min, item) => Math.min(min, item.perfectOrderLineId ?? 0), 0);
      if (nextTempId >= 0) nextTempId = -1;

      const addedItems = pickedItems.map((picked) => ({
        perfectOrderLineId: nextTempId--,
        perfectOrderId: prev.perfectOrderId,
        itemNumber: picked.itemNumber,
        vendorNumber: picked.vendorNumber,
        description: picked.description,
        quantity: picked.quantity,
        customerPrice: picked.customerPrice,
        kitParentItemNumber: picked.kitParentItemNumber,
      }));

      return {
        ...prev,
        perfectOrderItems: [
          ...currentItems.map((item) =>
            isSamePerfectOrderItem(item, parentItem)
              ? {
                  ...item,
                  pendingDelete: true,
                }
              : item
          ),
          ...addedItems,
        ],
      };
    });
  };

  const handleRemoveAddedItem = (targetItem: PerfectOrderItem) => {
    if ((targetItem.perfectOrderLineId ?? 0) >= 0) return;
    setPerfectOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        perfectOrderItems: (prev.perfectOrderItems || []).filter((item) => !isSamePerfectOrderItem(item, targetItem)),
      };
    });
  };

  const handleToggleItemIncluded = (targetItem: PerfectOrderItem) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const restoringKitParent = targetItem.pendingDelete === true && !!targetItem.itemNumber && !targetItem.kitParentItemNumber;
      const parentItemNumber = targetItem.itemNumber?.toLowerCase();

      return {
        ...prev,
        perfectOrderItems: (prev.perfectOrderItems || []).map((item) =>
          isSamePerfectOrderItem(item, targetItem)
            ? { ...item, pendingDelete: item.pendingDelete !== true }
            : restoringKitParent && item.kitParentItemNumber?.toLowerCase() === parentItemNumber
              ? { ...item, pendingDelete: true }
              : item
        ),
      };
    });
  };

  const dropdownOptionsByType = useMemo(() => {
    const bySortOrder = (a: PerfectOrderDropdownValueResult, b: PerfectOrderDropdownValueResult) => {
      const sortDiff = (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999);
      if (sortDiff !== 0) {
        return sortDiff;
      }

      return (a.dropdownValueId ?? 0) - (b.dropdownValueId ?? 0);
    };

    const getSortedByType = (type: 'Status' | 'RootCause' | 'AssignedTo') => dropdownValues.filter((item) => item.dropdownType === type).sort(bySortOrder);

    return {
      status: getSortedByType('Status'),
      rootCause: getSortedByType('RootCause'),
    };
  }, [dropdownValues]);

  const claimTypeOptions = useMemo(() => {
    const activeReturnReasonClaimTypeIds = new Set(returnReasons.filter((reason) => reason.isActive !== false && reason.claimTypeId).map((reason) => reason.claimTypeId));

    return claimTypes
      .filter((claimType) => claimType.isActive !== false && activeReturnReasonClaimTypeIds.has(claimType.claimTypeId))
      .sort((a, b) => a.claimType.localeCompare(b.claimType));
  }, [claimTypes, returnReasons]);
  const claimTypeValue = claimTypeOptions.some((claimType) => claimType.claimTypeId === perfectOrder?.claimTypeId) ? (perfectOrder?.claimTypeId ?? '') : '';

  const filteredReturnReasons = useMemo(() => {
    if (!perfectOrder?.claimTypeId) {
      return [];
    }

    return filterPerfectOrderReturnReasons(returnReasons, claimTypes, perfectOrder.claimTypeId).sort((a, b) => a.returnReason.localeCompare(b.returnReason));
  }, [claimTypes, perfectOrder?.claimTypeId, returnReasons]);

  const combinedOrderNumber = perfectOrder ? `${perfectOrder.orderNumber}-${perfectOrder.orderSuffix}` : undefined;
  const invoiceAddons = perfectOrder?.invoiceAddons || [];
  const assignedToUsers = useMemo(() => getUniqueUsersByUsername(allSFAUsers, perfectOrder?.assignedTo), [allSFAUsers, perfectOrder?.assignedTo]);
  const assignedToUser = assignedToUsers.find((user) => user.companyUserId === perfectOrder?.assignedTo);
  const assignedToOptions = filteredAssignedToUsers ?? assignedToUsers;

  const getDropdownLabel = (type: 'Status' | 'RootCause' | 'AssignedTo', value?: number): string => {
    if (!value) {
      return '';
    }

    if (type === 'AssignedTo') {
      return assignedToUsers.find((user) => user.companyUserId === value)?.username || String(value);
    }

    return dropdownValues.find((item) => item.dropdownType === type && item.dropdownValueId === value)?.dropdownValue || String(value);
  };

  const getClaimTypeLabel = (value?: number): string => {
    if (!value) {
      return '';
    }

    return claimTypes.find((item) => item.claimTypeId === value)?.claimType || String(value);
  };

  const getReturnReasonLabel = (value?: number): string => {
    if (!value) {
      return '';
    }

    return returnReasons.find((item) => item.returnReasonId === value)?.returnReason || String(value);
  };

  const handleExportPerfectOrder = () => {
    if (!perfectOrder || !exportRef.current) {
      return;
    }

    const workbook = buildPerfectOrderWorkbook(perfectOrder, {
      combinedOrderNumber,
      formatPhone: formatPhoneNumber,
      getDropdownLabel,
      getClaimTypeLabel,
      getReturnReasonLabel,
    });

    exportRef.current.save(workbook);
  };

  const handleAppendNote = () => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const currentNotes = prev.perfectOrderNotes || [];
      const minId = currentNotes.reduce((min, item) => Math.min(min, item.perfectOrderNoteId || 0), 0);
      const tempId = minId <= 0 ? minId - 1 : -1;

      return {
        ...prev,
        perfectOrderNotes: [
          ...currentNotes,
          {
            perfectOrderNoteId: tempId,
            perfectOrderId: prev.perfectOrderId,
            note: '',
            createdBy: null,
            createDate: new Date().toISOString(),
          },
        ],
      };
    });
  };

  const handleNoteChange = (perfectOrderNoteId: number, value: string) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const currentNotes = prev.perfectOrderNotes || [];
      return {
        ...prev,
        perfectOrderNotes: currentNotes.map((note) =>
          note.perfectOrderNoteId === perfectOrderNoteId
            ? {
                ...note,
                note: value,
              }
            : note
        ),
      };
    });
  };

  const refreshPerfectOrder = async (perfectOrderId: number) => {
    const refreshed = await orderApi.getPerfectOrder(perfectOrderId);
    const normalized = normalizePerfectOrder(refreshed);
    setPerfectOrder(normalized);
  };

  const handleUploadFiles = async (files: File[]) => {
    if (!perfectOrder || files.length === 0) {
      return;
    }

    setUploadingFiles(true);
    try {
      await orderApi.uploadPerfectOrderFiles(perfectOrder.perfectOrderId, files);
      await refreshPerfectOrder(perfectOrder.perfectOrderId);
      openSnackBar('Attachment uploaded successfully.', 'success', 2500);
    } catch (error) {
      openSnackBar('Unable to upload attachment.', 'error', 4000);
      Logger.error(`Error uploading files for Perfect Order ${perfectOrder.perfectOrderId}`, error);
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteFile = async (file: PerfectOrderFileResponse) => {
    if (!perfectOrder) {
      return;
    }

    setDeletingFileId(file.perfectOrderFileId);
    try {
      await orderApi.deletePerfectOrderFile(perfectOrder.perfectOrderId, file.perfectOrderFileId);
      await refreshPerfectOrder(perfectOrder.perfectOrderId);
      openSnackBar('Attachment deleted successfully.', 'success', 2500);
    } catch (error) {
      openSnackBar('Unable to delete attachment.', 'error', 4000);
      Logger.error(`Error deleting file ${file.perfectOrderFileId} for Perfect Order ${perfectOrder.perfectOrderId}`, error);
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleSaveNote = async (tempNoteId: number) => {
    if (!perfectOrder) {
      return;
    }

    const note = (perfectOrder?.perfectOrderNotes || []).find((n) => n.perfectOrderNoteId === tempNoteId);
    if (!note || !(note.note || '').trim()) {
      return;
    }

    try {
      await orderApi.createPerfectOrderNote(perfectOrder.perfectOrderId, {
        Note: note.note.trim(),
      });

      await refreshPerfectOrder(perfectOrder.perfectOrderId);
    } catch (error) {
      openSnackBar('Unable to save note.', 'error', 4000);
      Logger.error(`Error saving note for Perfect Order ${perfectOrder.perfectOrderId}`, error);
    }
  };

  const handleUpdateNote = async (perfectOrderNoteId: number, value: string) => {
    if (!perfectOrder) {
      return;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }

    try {
      await orderApi.updatePerfectOrderNote(perfectOrder.perfectOrderId, perfectOrderNoteId, {
        Note: trimmed,
      });

      await refreshPerfectOrder(perfectOrder.perfectOrderId);
    } catch (error) {
      openSnackBar('Unable to update note.', 'error', 4000);
      Logger.error(`Error updating note ${perfectOrderNoteId} for Perfect Order ${perfectOrder.perfectOrderId}`, error);
    }
  };

  const handleCancelNewNote = (tempNoteId: number) => {
    setPerfectOrder((prev) => {
      if (!prev) {
        return prev;
      }

      const currentNotes = prev.perfectOrderNotes || [];
      return {
        ...prev,
        perfectOrderNotes: currentNotes.filter((note) => note.perfectOrderNoteId !== tempNoteId),
      };
    });
  };

  const handleSavePerfectOrder = async () => {
    if (!perfectOrder) {
      return;
    }

    setSaving(true);
    try {
      const removedItemKeys = new Set((perfectOrder.perfectOrderItems || []).filter((item) => item.pendingDelete === true).map(getPerfectOrderItemKey));
      const orderToSave: PerfectOrder = {
        ...perfectOrder,
        perfectOrderItems: normalizePerfectOrderItemsForSave(perfectOrder.perfectOrderItems),
      };

      console.log('Saving perfect order', orderToSave);

      await orderApi.savePerfectOrder(orderToSave);
      setPerfectOrder(normalizePerfectOrder(orderToSave));

      const notesToCreate = (perfectOrder.perfectOrderNotes || []).filter((note) => {
        return (note.perfectOrderNoteId ?? 0) <= 0 && (note.note || '').trim().length > 0;
      });

      if (notesToCreate.length > 0) {
        await Promise.all(
          notesToCreate.map((note) =>
            orderApi.createPerfectOrderNote(perfectOrder.perfectOrderId, {
              Note: (note.note || '').trim(),
            })
          )
        );
      }

      await refreshPerfectOrder(perfectOrder.perfectOrderId);
      if (removedItemKeys.size > 0) {
        setPerfectOrder((refreshedOrder) =>
          refreshedOrder
            ? {
                ...refreshedOrder,
                perfectOrderItems: refreshedOrder.perfectOrderItems?.filter((item) => !removedItemKeys.has(getPerfectOrderItemKey(item))),
              }
            : refreshedOrder
        );
      }
      openSnackBar('Perfect Order saved successfully.', 'success', 2500);
    } catch (error) {
      openSnackBar('Unable to save Perfect Order.', 'error', 4000);
      Logger.error(`Error saving Perfect Order ${perfectOrder.perfectOrderId}`, error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 2 }}>
      <Container maxWidth={getSettingsWidth(contentWidth)} className={`container-width__${contentWidth} perfect-order-detail-page`}>
        <Grid container spacing={2}>
          <Grid style={{ width: '100%' }} size={{ xs: 12 }}>
            <OrderEntryTopNav buttonList={['back']} backPath="/sales/PerfectOrders" backButtonText="Back to Perfect Orders" />
          </Grid>

          {!loading && perfectOrder && (
            <>
              <Grid style={{ width: '100%' }} size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'end', gap: 2 }}>
                    <h3 className="mb-0">
                      Perfect Order {perfectOrder.perfectOrderId}
                      {perfectOrder.transactionType ? ` (${perfectOrder.transactionType.toUpperCase()})` : ''}
                    </h3>
                    {!permissionToEdit && <p className="mb-0">(Read-only)</p>}
                  </Box>

                  <Box sx={{ gap: 2, display: 'flex' }}>
                    <Button title="Export Excel" variant="outlined" size="small" onClick={handleExportPerfectOrder}>
                      Export to Excel
                    </Button>

                    <Button variant="contained" onClick={handleSavePerfectOrder} disabled={!permissionToEdit || saving}>
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </Grid>

              <Divider sx={{ width: '100%', mt: 1 }} />

              <PerfectOrderItemGrid
                items={perfectOrder.perfectOrderItems || []}
                onItemChange={handleItemFieldChange}
                onToggleItemIncluded={handleToggleItemIncluded}
                onAddItem={handleAppendItem}
                onAddKitItems={handleAppendKitItems}
                onRemoveAddedItem={handleRemoveAddedItem}
                customerNumber={perfectOrder.customerNumber !== undefined ? String(perfectOrder.customerNumber) : undefined}
                warehouseCode={perfectOrder.warehouse}
                shipToNumber={perfectOrder.shipTo}
                disabled={!permissionToEdit || saving}
              />
            </>
          )}

          {loading && (
            <Grid style={{ width: '100%', display: 'flex', justifyContent: 'center' }} size={{ xs: 12 }}>
              <CircularProgress />
            </Grid>
          )}

          {!loading && loadError && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">{loadError}</Alert>
            </Grid>
          )}

          {!loading && perfectOrder && (
            <>
              <Grid style={{ width: '100%' }} size={{ xs: 12 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Card>
                      <CardContent>
                        {/* TMP: Hide Compliance Banner */}
                        {/* {compliance && (
                          <Grid size={{ xs: 12 }}>
                            <Alert severity={!compliance.hasSelectedItems ? 'error' : compliance.disposition === 'Destroy in Field' ? 'info' : 'warning'} sx={{ mb: 2 }}>
                              <AlertTitle>
                                {compliance.hasSelectedItems ? (
                                  <>
                                    Disposition Notice: <b>{compliance.disposition}</b>
                                  </>
                                ) : (
                                  'Selection Notice'
                                )}
                              </AlertTitle>
                              {compliance.reasons.map((reason, idx) => (
                                <div key={idx}>• {reason}</div>
                              ))}
                            </Alert>
                          </Grid>
                        )} */}

                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
                          <DateField label="Opened" value={perfectOrder.dateOpened} emptyText="" />
                          <Divider orientation="vertical" variant="middle" flexItem />
                          <DateField label="Invoiced" value={perfectOrder.invoicedDate} emptyText="" />
                          <Divider orientation="vertical" variant="middle" flexItem />
                          <DateField label="Shipped " value={perfectOrder.shippedDate} emptyText="" />
                          <Divider orientation="vertical" variant="middle" flexItem />
                          <DateField label="Closed" value={perfectOrder.dateClosed} emptyText="" />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2} className="view-order__row">
                          <Grid size={{ xs: 12, md: 6 }}>
                            <div>
                              <Field label="Order Number" value={combinedOrderNumber} />
                              <Field label="Customer" value={`${customer?.name} (${perfectOrder.customerNumber})`} />
                              <Field label="Customer Contact Name" value={perfectOrder.customerContactName} />
                              <Field label="Customer Contact Phone" value={formatPhoneNumber(perfectOrder.customerContactPhone)} />
                              <Field label="Customer Contact Email" value={perfectOrder.customerContactEmail} />
                              <Field label="Requester Email" value={perfectOrder.requesterEmail} />
                              <Field label="Ship To" value={perfectOrder.shipTo} />
                              <Field label="Warehouse" value={perfectOrder.warehouse} />
                              <Field label="Backup Warehouse" value={perfectOrder.backupWarehouse} />
                              <Field label="Transaction Type" value={perfectOrder.transactionType} />
                              <Field label="Comment" value={perfectOrder.requesterComment} />
                            </div>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <div>
                              <strong>Sales Reps:</strong>
                              {customerReps.length > 0 ? (
                                <Box>
                                  {customerReps.map((rep) => (
                                    <div key={rep.label}>
                                      <strong>{rep.label}:</strong> {rep.value}
                                    </div>
                                  ))}
                                </Box>
                              ) : (
                                <span className="view-order__column__val px-1">N/A</span>
                              )}
                            </div>
                          </Grid>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ mt: 0 }}>
                          <PerfectOrderInvoiceAddonsGrid addons={invoiceAddons} />
                        </Grid>
                      </CardContent>
                    </Card>

                    <Box sx={{ mt: 3 }}>
                      <PerfectOrderNotesGrid
                        disabled={!permissionToEdit}
                        notes={perfectOrder.perfectOrderNotes || []}
                        users={allSFAUsers}
                        onNoteChange={handleNoteChange}
                        onAppendNote={handleAppendNote}
                        onSaveNote={handleSaveNote}
                        onCancelNewNote={handleCancelNewNote}
                        onUpdateNote={handleUpdateNote}
                      />
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, md: 5 }}>
                    <Card>
                      <CardContent sx={{ pb: 0, mb: 0 }} style={{ paddingBottom: '0 !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box>
                            <Tooltip title={!permissionToEdit ? 'Only Customer Service can edit this menu' : ''}>
                              <span>
                                <DropdownField
                                  label="Status"
                                  value={perfectOrder.statusId ?? ''}
                                  options={dropdownOptionsByType.status}
                                  onChange={(e) => handleDropdownFieldChange('statusId', e.target.value === '' ? '' : Number(e.target.value))}
                                  disabled={!permissionToEdit}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Grid container spacing={1.5} className="view-order__row">
                          <Grid size={{ xs: 12 }}>
                            <div>
                              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <FormControl fullWidth size="small" sx={{ my: 0.75 }}>
                                  <InputLabel>Claim Type</InputLabel>
                                  <Select<number | ''>
                                    disabled={!permissionToEdit}
                                    value={claimTypeValue}
                                    label="Claim Type"
                                    onChange={(e) => handleClaimTypeChange(e.target.value === '' ? '' : Number(e.target.value))}
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    {claimTypeOptions.map((claimType) => (
                                      <MenuItem key={claimType.claimTypeId} value={claimType.claimTypeId}>
                                        {claimType.claimType}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                <FormControl fullWidth size="small" sx={{ my: 0.75 }} disabled={!perfectOrder.claimTypeId || filteredReturnReasons.length === 0}>
                                  <InputLabel>Return Reason</InputLabel>
                                  <Select<number | ''>
                                    disabled={!permissionToEdit}
                                    value={perfectOrder.returnReasonId ?? ''}
                                    label="Return Reason"
                                    onChange={(e) => handleReturnReasonChange(e.target.value === '' ? '' : Number(e.target.value))}
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    {filteredReturnReasons.map((reason) => (
                                      <MenuItem key={reason.returnReasonId} value={reason.returnReasonId}>
                                        {reason.returnReason}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              <TypeAhead
                                defaultValue={assignedToUser}
                                sx={{ width: '100%', my: 0.75 }}
                                data={assignedToOptions}
                                minFilterLength={1}
                                onChange={handleAssignedToChange}
                                onFilterChange={handleAssignedToFilterChange}
                                getOptionLabel={(option: UserSimple) => option.username}
                                getOptionKey={(option: UserSimple) => option.companyUserId}
                                placeholder="Assigned To"
                                title="Assign this Perfect Order to a user"
                                disabled={!permissionToEdit}
                              />
                              <TextField
                                disabled={!permissionToEdit}
                                fullWidth
                                size="small"
                                sx={{ my: 0.75 }}
                                label="Buyer"
                                value={perfectOrder.buyer ?? ''}
                                onChange={(e) => handleTextFieldChange('buyer', e.target.value)}
                              />
                              <DropdownField
                                disabled={!permissionToEdit}
                                label="Root Cause"
                                value={perfectOrder.rootCauseId ?? ''}
                                options={dropdownOptionsByType.rootCause}
                                onChange={(e) => handleDropdownFieldChange('rootCauseId', e.target.value === '' ? '' : Number(e.target.value))}
                              />
                              <BooleanCheckboxField
                                label="DNS Needed"
                                value={perfectOrder.isDnsNeeded}
                                onChange={(value) => handleBooleanFieldChange('isDnsNeeded', value)}
                                disabled={!permissionToEdit}
                                // disabled={!permissionToEdit || (compliance && !compliance.isPickupEligible)}
                                helperText={compliance && !compliance.isPickupEligible ? 'Ineligible: Request is > 30 days past delivery.' : undefined}
                              >
                                {perfectOrder.isDnsNeeded && (
                                  <RepeaterField
                                    label="DNS #"
                                    addLabel="Add DNS #"
                                    removeAriaLabel="Remove DNS number"
                                    value={perfectOrder.dnsNumber ?? ''}
                                    onChange={(joined) => handleRepeaterFieldChange('dnsNumber', joined)}
                                  />
                                )}
                              </BooleanCheckboxField>
                              <BooleanCheckboxField
                                label="Credit Needed"
                                value={perfectOrder.isCreditNeeded}
                                onChange={(value) => handleBooleanFieldChange('isCreditNeeded', value)}
                                // disabled={!permissionToEdit || !!creditEligibilityReason}
                                disabled={!permissionToEdit}
                                helperText={creditEligibilityReason}
                              >
                                {perfectOrder.isCreditNeeded && (
                                  <RepeaterField
                                    label="Credit #"
                                    addLabel="Add credit #"
                                    removeAriaLabel="Remove credit number"
                                    value={perfectOrder.creditNumber ?? ''}
                                    onChange={(joined) => handleRepeaterFieldChange('creditNumber', joined)}
                                  />
                                )}
                              </BooleanCheckboxField>
                            </div>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ mt: 1 }}>
                        <PerfectOrderFilesGrid
                          disabled={!permissionToEdit || saving}
                          files={perfectOrder.perfectOrderFiles || []}
                          onUploadFiles={handleUploadFiles}
                          onDeleteFile={handleDeleteFile}
                          uploading={uploadingFiles}
                          deletingFileId={deletingFileId}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <ExcelExport ref={exportRef} fileName={`PerfectOrder_${perfectOrder.perfectOrderId}.xlsx`} />
              </Grid>
            </>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default PerfectOrderDetail;
